import React, {useState, useEffect } from 'react';
import PropTypes from 'prop-types';
import { useParams } from 'react-router';
import { connect, useDispatch } from 'react-redux';
import { API_PREFIX as API, API_ROUTES } from '../../constants/api_routes';
import { Button, Container, Grid, makeStyles, Typography } from '@material-ui/core';
import { useForm } from 'react-hook-form';
import { ErrorMessage } from '@hookform/error-message';
import { getCustomer, getCustomers, postCustomer, putCustomer } from '../../store/actions/customer/customer_action';
import { REACT_ROUTES } from '../../constants/react_routes';
import history from '../../services/history';
import TextInput from '../ui_components/text_input';
import { useSnackbar } from 'notistack';
import Autocomplete from '../ui_components/autocomplete';
import DatePicker from '../ui_components/datepicker_controller';
import { format, isValid } from 'date-fns';

const useStyles = makeStyles((theme) => ({
	container: {
		padding: theme.spacing(3, 2)
	},
    typography: {
		margin: theme.spacing(3, 0, 2)
    },
    form: {
		width: '100%', // Fix IE 11 issue.
		marginTop: theme.spacing(1),
        '& p': {
            color: '#bf1650',
            '&::before': {
                display: 'inline',
                content: '"⚠ "'
            }
        }
	}
}));

// const setFormValues = (object, setValue) => {
//     for (const [key, value] of Object.entries(object)) {
//         setValue(key, value, { shouldDirty: true, shouldValidate: true });
//     }
// }

const showErrorsAndMessages = (violations, enqueueSnackbar, setError) => {
    if (!violations) {
        enqueueSnackbar('Uspešno ste sačuvali klijenta', { variant: 'success' });
        return history.push(REACT_ROUTES.CUSTOMERS);
    }
    for (const violation of violations) {
        enqueueSnackbar(violation?.message, { variant: 'warning' });
        if (violation?.propertyPath) {
            setError(violation?.propertyPath, { message: violation?.message });
        }
    }
}

const Customer = ({ customers, handleClose, initialize }) => {
    const { uuid } = useParams();
    const { container, typography, form } = useStyles();
    const { register, control, setValue, handleSubmit, reset, setError, formState: { errors } } = useForm();
    const dispatch = useDispatch();

    const { enqueueSnackbar } = useSnackbar();
    const [worker, setWorker] = useState(null);
    const [birthday, setBirthday] = useState(null);

    useEffect(() => {
        register('worker');
        uuid && (async () => {
            const customer = await getCustomer(uuid);
            reset(customer);
            if (customer?.birthday) {
                setBirthday(customer?.birthday);
                setValue('birthday', customer?.birthday);
            }
            if (customer?.worker?.uuid) {
                const workerIri = `${API}${API_ROUTES.WORKERS}/${customer?.worker?.uuid}`;
                setWorker(workerIri);
                setValue('worker', workerIri);
            }
        })();
        !customers.collection.length && dispatch(getCustomers());
    }, [uuid]);

    const onSubmit = async (data) => {
        if (uuid && !handleClose && !initialize) {
            const { violations } = await dispatch(putCustomer(uuid, data));
            return showErrorsAndMessages(violations, enqueueSnackbar, setError);
        }
        const response = await dispatch(postCustomer(data));
        const { violations } = response;
        if (!violations && handleClose && initialize) {
            initialize(response);
            return handleClose();
        }
        return showErrorsAndMessages(violations, enqueueSnackbar, setError);
    }

    const handleClickBackToList = () => {
        handleClose ? handleClose() : history.push(REACT_ROUTES.CUSTOMERS);
    }

    const handleWorkerObject = (object) => {
        setWorker(object);
        setValue('worker', object);
    }

    const handleBirthdayChange = (e) => {
        let birthday = e;
        if (isValid(birthday)) {
            birthday = format(birthday, 'M/d/yyyy');
        }
        setBirthday(birthday);
        setValue('birthday', birthday);
    }

    return (
        <Container className={container}>
            <Typography align="center" variant="h4" className={typography}>Klijent</Typography>
            <Grid container justify="center" spacing={3}>
                <Grid item>
                    <Button
                        color="primary"
                        size="small"
                        variant="contained"
                        onClick={handleSubmit(onSubmit)}
                        type="button"
                    >
                        Sačuvaj
                    </Button>
                </Grid>
                <Grid item>
                    <Button
                        variant="contained"
                        size="small"
                        color="primary"
                        onClick={handleClickBackToList}
                    >
                        Nazad
                    </Button>
                </Grid>
            </Grid>
            <form className={form}>
                <Grid container justify="center" spacing={3}>
                    <Grid item xs={8}>
                        <TextInput
                            capitalize
                            control={control}
                            name="firstName"
                            label="Ime"
                            rules={{ required: 'Unesite ime' }}
                        />
                        <ErrorMessage errors={errors} name="firstName" as="p" />
                    </Grid>
                    <Grid item xs={8}>
                        <TextInput
                            capitalize
                            control={control}
                            name="lastName"
                            label="Prezime"
                            rules={{ required: 'Unesite prezime' }}
                        />
                        <ErrorMessage errors={errors} name="lastName" as="p" />
                    </Grid>
                    <Grid item xs={8}>
                        <TextInput
                            capitalize
                            control={control}
                            name="shortName"
                            label="Nadimak"
                        />
                    </Grid>
                    <Grid item xs={8}>
                        <TextInput
                            control={control}
                            name="phone"
                            label="Telefon"
                            rules={{ required: 'Unesite telefon' }}
                        />
                        <ErrorMessage errors={errors} name="phone" as="p" />
                    </Grid>
                    <Grid item xs={8}>
                        <Autocomplete
                            name="worker"
                            label="Radnik"
                            placeholder="Radnik"
                            handleObject={handleWorkerObject}
                            api={API_ROUTES.WORKERS}
                            preloadValue={worker}
                        />
                    </Grid>
                    <Grid item xs={8}>
                        <TextInput
                            control={control}
                            name="email"
                            label="Email"
                        />
                    </Grid>
                    <Grid item xs={8}>
                        <TextInput
                            control={control}
                            name="address"
                            label="Adresa"
                        />
                    </Grid>
                    <Grid item xs={8}>
                        <TextInput
                            control={control}
                            name="description"
                            label="Napomena"
                        />
                    </Grid>
                    <Grid item xs={8}>
                        <TextInput
                            control={control}
                            name="jmbg"
                            label="JMBG"
                        />
                    </Grid>
                    <Grid item xs={8}>
                        <DatePicker
                            name="birthday"
                            control={control}
                            label="Datum rođenja"
                            value={birthday}
                            onChange={handleBirthdayChange}
                        />
                    </Grid>
                </Grid>
            </form>
        </Container>
    )
}

Customer.propTypes = {
    customers: PropTypes.object,
    handleClose: PropTypes.func,
    initialize: PropTypes.func
}

const mapStateToProps = (state) => ({
    customers: state.customers
});

export default connect(mapStateToProps)(Customer);
